/** @jsx jsx */
import React from 'react';
import { jsx } from '@balance-web/core';
import { ActionButton } from '@balance-web/button';
import { Fieldset } from '@balance-web/fieldset';
import { Form } from '@balance-web/forms';
import { Stack } from '@balance-web/stack';
import { Notice } from '@balance-web/notice';
import { Text } from '@balance-web/text';
import { Button } from '@balance-web/button';
import { Columns } from '@balance-web/columns';
import { Divider } from '@balance-web/divider';
import { Heading } from '@balance-web/heading';
import { TextLink } from '@balance-web/text-link';

import { SelectField } from '../../generic/SelectField';
import { TextField } from '../../generic/TextField';
import { SingleSignOnButton } from '../signIn/singleSignOnButtons/singleSignOnButton';
import {
  AppleIcon,
  GoogleIcon,
  MicrosoftIcon,
} from '../signIn/singleSignOnButtons/singleSignOnButton/Icons';
import { ErrorResponseType } from '../../useErrorResponses';
import { PasswordValidityCalculator } from '../../passwordStrength/passwordValidityCalculator';

import { SingupFormSchema } from './formSchema';

export type SignUpFormProps = {
  form: Form<typeof SingupFormSchema>;
  alreadyRegistered?: boolean;
  submitting?: boolean;
  onSubmit: () => void;
  onSignInWithAppleClick?: () => void;
  onSignInWithMicrosoftClick?: () => void;
  onSigninWithGoogleClick?: () => void;
  onLoginClick: () => void;
  error?: ErrorResponseType;
  email?: string;
  inviteId?: string;
};

const countries = [
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
];

export const SignUpForm: React.FC<SignUpFormProps> = (props) => {
  const {
    alreadyRegistered,
    form,
    submitting,
    onSubmit,
    onSignInWithAppleClick,
    onSignInWithMicrosoftClick,
    onSigninWithGoogleClick,
    onLoginClick,
    error,
    email,
    inviteId,
  } = props;

  return (
    <Stack margin="xxlarge" gap="xlarge">
      <>
        <Heading align="center" level="5">
          {inviteId && email
            ? 'Complete account details'
            : 'Create a new Reckon account'}
        </Heading>
        {error && <Notice tone={error.tone}>{error.description}</Notice>}
        {alreadyRegistered && (
          <Notice tone="cautious" size="small">
            <Text size="small">
              The provided email address is associated with a Reckon account.
              Please sign in or use different email address.
            </Text>
            {!inviteId && (
              <ActionButton label="Sign in" onClick={onLoginClick} />
            )}
          </Notice>
        )}
        <form onSubmit={onSubmit}>
          <Stack gap="large">
            <Fieldset legend="Your name" legendAppearance="label">
              <Columns gap="small">
                <TextField
                  labelVisible={false}
                  label="First name"
                  placeholder="Enter first Name"
                  {...form.fields.firstname.props}
                />
                <TextField
                  label="Last name"
                  placeholder="Enter last name"
                  labelVisible={false}
                  {...form.fields.lastname.props}
                />
              </Columns>
            </Fieldset>
            <TextField
              type="email"
              label="Email address"
              placeholder="Enter email"
              {...(inviteId && email ? { disabled: true } : {})}
              {...form.fields.email.props}
            />
            <SelectField
              options={countries}
              label="Home country"
              placeholder="Select an option"
              {...form.fields.country.props}
            />
            <TextField
              type="tel"
              label="Contact number"
              placeholder="Enter contact number"
              {...form.fields.phonenumber.props}
            />

            <TextField
              type="password"
              label="Password"
              placeholder="Create password"
              {...form.fields.password.props}
            />
            <PasswordValidityCalculator
              password={form.fields.password.props.value}
            />
            <Button
              type="submit"
              label="Continue"
              loading={submitting}
              block
              disabled={form.validity !== 'valid'}
            />
          </Stack>
        </form>
      </>

      <Text align="center">- OR -</Text>
      <Stack gap="small">
        {onSigninWithGoogleClick && (
          <SingleSignOnButton
            label="Continue with Google"
            icon={<GoogleIcon />}
            onClick={onSigninWithGoogleClick}
          />
        )}
        {onSignInWithMicrosoftClick && (
          <SingleSignOnButton
            label="Continue with Microsoft"
            icon={<MicrosoftIcon />}
            onClick={onSignInWithMicrosoftClick}
          />
        )}

        {onSignInWithAppleClick && (
          <SingleSignOnButton
            label="Continue with Apple"
            icon={<AppleIcon />}
            onClick={onSignInWithAppleClick}
          />
        )}
      </Stack>

      {!inviteId && (
        <Stack>
          <Divider />
          <Text>
            Already have Reckon account?&nbsp;
            <TextLink href="/login">Sign in now</TextLink>
          </Text>
        </Stack>
      )}
    </Stack>
  );
};
